/**
 * The designer's review of a run, read from the run's `report.md`
 * (`walkthroughs.report_markdown`), until walkthrough push stores it as
 * `walkthroughs.designer_review` (designer-findings task 3.6). An older
 * CLI titled the section "UX perspective".
 */

import type { DesignerReview } from "../schemas/walkthrough-bundle.js";

export interface ReportDesignerRuleBroken {
  /** The design-doc rule name, e.g. "A signer sees what they sign". */
  rule: string;
  /** The file where the rule lives, e.g. "DESIGN.md". */
  file: string | null;
  /** Explanation of how the rule was broken on the walk. */
  detail: string | null;
  /** Screen / step location where the violation occurred, e.g. "/p/sjoviken, steps 1, 16". */
  where: string | null;
}

export interface ReportDesignerCause {
  /** The persona finding title this design cause explains. */
  finding: string;
  /** Designer finding ID in the report (e.g. "D1"). */
  code?: string | null;
  /** Severity of the design cause (e.g. "major"). */
  severity?: string | null;
  /** The design-doc rule broken by this cause, if any. */
  breaksRule?: string | null;
  /** The explanation after the colon. */
  detail?: string | null;
  /** Raw markdown line (kept for backward compatibility). */
  markdown: string;
}

export interface ReportDesignerReview {
  /** The section's own heading: "Designer's review", or the older "UX perspective". */
  heading: string;
  /** What the review was judged against, and by whom (the italic first line). */
  label: string | null;
  /** The narrative summary paragraph(s) of the designer review (without duplicate finding bullets). */
  summary?: string;
  /** Structured list of design-doc rules broken during the run. */
  rulesBroken?: ReportDesignerRuleBroken[];
  /** Tally of broken vs held rules, e.g. { broken: 5, held: 3 }. */
  rulesTally?: { broken: number; held: number } | null;
  /** The rest of the section: summary, rules, findings by screen. */
  markdown: string;
  /**
   * Designer findings that explain a persona finding, which the report
   * nests under that finding instead of this section.
   */
  designCauses: ReportDesignerCause[];
}

const SECTION = /^## (Designer's review|UX perspective)[ \t]*$/m;

/** A section's body: from after its heading to the next `## ` heading. */
function sectionBody(markdown: string, at: number, headingLength: number) {
  const rest = markdown.slice(at + headingLength);
  const next = /^## \S/m.exec(rest);
  return next ? rest.slice(0, next.index) : rest;
}

/**
 * Markdown emphasis as plain text: the dashboard keeps italics for
 * quotations, and nothing in the designer's review is one.
 */
function withoutItalics(text: string): string {
  return text.replace(
    /(^|[\s(])_([^_\n]+)_(?=$|[\s).,;:])/gm,
    (_m, lead: string, inner: string) => `${lead}${inner}`,
  );
}

function parseRuleBrokenBullet(
  rawLine: string,
): ReportDesignerRuleBroken | null {
  const body = rawLine.replace(/^-\s+/, "").trim();
  if (!body) return null;

  const quoted = /^["“]([^"”]+)["”](?:\s*\(([^)]+)\))?(?::\s*(.+))?$/.exec(
    body,
  );
  const unquoted = !quoted
    ? /^([^(:]+?)(?:\s*\(([^)]+)\))?(?::\s*(.+))?$/.exec(body)
    : null;
  const match = quoted ?? unquoted;
  if (!match) return null;

  const rule = match[1].trim();
  const file = match[2]?.trim() || null;
  let detail = match[3]?.trim() || null;
  let where: string | null = null;

  if (detail) {
    const parenCount = detail.match(/\(on\s+[^)]+\)/g)?.length ?? 0;
    if (parenCount === 1) {
      const parenMatch = /^(.*?)\s*\(on\s+([^)]+)\)\.?\s*$/.exec(detail);
      if (parenMatch) {
        detail = parenMatch[1].trim() || null;
        where = parenMatch[2].trim();
      }
    } else {
      const commaCount = detail.match(/,\s*on\s+\/\S+/g)?.length ?? 0;
      if (commaCount === 1) {
        const commaMatch =
          /^(.*?),\s*on\s+(\/\S+(?:,\s*steps?\s+[\d,\s]+)?)\.?\s*$/.exec(
            detail,
          );
        if (commaMatch) {
          detail = commaMatch[1].trim() || null;
          where = commaMatch[2].trim();
        }
      }
    }
  }

  return { rule, file, detail, where };
}

function parseStructuredSection(markdown: string): {
  summary: string;
  rulesBroken: ReportDesignerRuleBroken[];
  rulesTally: { broken: number; held: number } | null;
} {
  const lines = markdown.split("\n");
  const summaryLines: string[] = [];
  const rulesBroken: ReportDesignerRuleBroken[] = [];
  let rulesTally: { broken: number; held: number } | null = null;
  let mode: "summary" | "rules" | "findings" = "summary";

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Stop collecting summary/rules once we hit the per-screen designer findings
    // list (which is already rendered as rows in the Findings table).
    if (
      /^\d+\s+designer findings?\b/i.test(line) ||
      /^###\s+/.test(line) ||
      /^-\s+D\d+\s+\*\*/.test(line)
    ) {
      mode = "findings";
      continue;
    }

    if (mode === "findings") continue;

    if (/^\*\*Rules broken\*\*$/i.test(line)) {
      mode = "rules";
      continue;
    }

    const bothTally = /^(\d+)\s+rules?\s+broken,\s+(\d+)\s+held\.?$/i.exec(
      line,
    );
    if (bothTally) {
      rulesTally = {
        broken: Number(bothTally[1]),
        held: Number(bothTally[2]),
      };
      continue;
    }

    const heldOnlyTally = /^(?:All\s+)?(\d+)\s+rules?\s+held\.?$/i.exec(line);
    if (heldOnlyTally) {
      rulesTally = {
        broken: 0,
        held: Number(heldOnlyTally[1]),
      };
      continue;
    }

    if (mode === "rules" && line.startsWith("- ")) {
      const parsed = parseRuleBrokenBullet(line);
      if (parsed) {
        rulesBroken.push(parsed);
        continue;
      }
    }

    if (mode === "summary") {
      summaryLines.push(rawLine);
    }
  }

  return {
    summary: summaryLines.join("\n").trim(),
    rulesBroken,
    rulesTally,
  };
}

function parseDesignCauseLine(
  finding: string,
  rawLine: string,
): ReportDesignerCause {
  const cleaned = withoutItalics(rawLine.trim());
  const match =
    /^\*\*Design cause\*\*\s+(?:(D\d+)\s+)?\(([^)]+)\):\s*(.+)$/.exec(cleaned);
  if (!match) {
    return { finding, markdown: cleaned };
  }
  const code = match[1]?.trim() || null;
  const parts = match[2].split("·").map((s) => s.trim());
  let severity: string | null = null;
  let breaksRule: string | null = null;
  for (const part of parts) {
    if (/^(critical|major|minor|nit)$/i.test(part)) {
      severity = part.toLowerCase();
    } else {
      const breaksMatch = /^breaks\s+["“]([^"”]+)["”]$/i.exec(part);
      if (breaksMatch) {
        breaksRule = breaksMatch[1].trim();
      }
    }
  }
  return {
    finding,
    code,
    severity,
    breaksRule,
    detail: match[3].trim(),
    markdown: cleaned,
  };
}

/** The design causes the Findings section nests under each finding. */
function designCauses(markdown: string): ReportDesignerReview["designCauses"] {
  const findings = /^## Findings[ \t]*$/m.exec(markdown);
  if (!findings) return [];
  const body = sectionBody(markdown, findings.index, findings[0].length);
  const out: ReportDesignerReview["designCauses"] = [];
  let finding: string | null = null;
  for (const line of body.split("\n")) {
    const heading = /^### (?:\d+\.\s+)?(.+?)\s*$/.exec(line);
    if (heading) {
      finding = heading[1];
      continue;
    }
    if (finding && line.startsWith("**Design cause**")) {
      out.push(parseDesignCauseLine(finding, line));
    }
  }
  return out;
}

function fromStoredDesignerReview(
  stored: DesignerReview,
): ReportDesignerReview {
  if (stored.skipped) {
    return {
      heading: "Designer's review",
      label: `Designer's review skipped: ${stored.skipped}.`,
      summary: "",
      rulesBroken: [],
      rulesTally: null,
      markdown: "",
      designCauses: [],
    };
  }
  const against =
    stored.design_files && stored.design_files.length > 0
      ? `the project's design docs (${stored.design_files.join(", ")})`
      : "general craft";
  const by = stored.judge ? ` by ${stored.judge}` : "";
  const agent = stored.dispatch === "subagent" ? ", a separate agent" : "";
  const label =
    stored.judge || (stored.design_files && stored.design_files.length > 0)
      ? `Judged against ${against}${by}${agent}.`
      : null;

  const rules = stored.rules ?? [];
  const broken = rules.filter((r) => !r.holds);
  const held = rules.filter((r) => r.holds);
  const rulesBroken: ReportDesignerRuleBroken[] = broken.map((r) => {
    const rec = r as Record<string, unknown>;
    const detail =
      typeof rec.note === "string"
        ? rec.note
        : typeof rec.why === "string"
          ? rec.why
          : typeof rec.detail === "string"
            ? rec.detail
            : null;
    const stepPart =
      r.steps && r.steps.length > 0
        ? `step${r.steps.length > 1 ? "s" : ""} ${r.steps.join(", ")}`
        : null;
    const where = [r.screen, stepPart].filter(Boolean).join(", ") || null;
    return {
      rule: r.rule,
      file: r.file ?? null,
      detail,
      where,
    };
  });

  return {
    heading: "Designer's review",
    label,
    summary: stored.summary?.trim() ?? "",
    rulesBroken,
    rulesTally:
      rules.length > 0 ? { broken: broken.length, held: held.length } : null,
    markdown: stored.summary?.trim() ?? "",
    designCauses: [],
  };
}

/** The designer's review in a run's report (or stored jsonb), or null when it has none. */
export function designerReviewFromReport(
  reportMarkdown: string | null | undefined,
  storedReview?: DesignerReview | null,
): ReportDesignerReview | null {
  if (!reportMarkdown) {
    return storedReview ? fromStoredDesignerReview(storedReview) : null;
  }
  const markdown = reportMarkdown.replace(/\r\n?/g, "\n");
  const section = SECTION.exec(markdown);
  if (!section) {
    return storedReview ? fromStoredDesignerReview(storedReview) : null;
  }
  const body = sectionBody(markdown, section.index, section[0].length).trim();
  const [head, ...tail] = body.split("\n");
  const first = /^_(.+)_$/.exec(head ?? "");
  const rest = first ? tail.join("\n") : body;
  const cleanedMarkdown = withoutItalics(rest.trim()).replace(
    "beyond the design causes above",
    "beyond the design causes below",
  );
  const structured = parseStructuredSection(cleanedMarkdown);
  const fallback = storedReview ? fromStoredDesignerReview(storedReview) : null;

  return {
    heading: section[1],
    label: first
      ? first[1].replace(/`/g, "").trim()
      : (fallback?.label ?? null),
    summary: structured.summary || fallback?.summary || "",
    rulesBroken:
      structured.rulesBroken.length > 0
        ? structured.rulesBroken
        : (fallback?.rulesBroken ?? []),
    rulesTally: structured.rulesTally ?? fallback?.rulesTally ?? null,
    // The dashboard lists the design causes after the section.
    markdown: cleanedMarkdown,
    designCauses: designCauses(markdown),
  };
}
